import React, { PropsWithChildren } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import packageJSON from "../../../../package.json";
import Video from "twilio-video";
import { useVideoState } from "../../../state";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";

interface AboutDialogProps {
  open: boolean;
  onClose(): void;
}

function AboutDialog({ open, onClose }: PropsWithChildren<AboutDialogProps>) {
  const { t } = useTranslation();
  const { roomType } = useVideoState();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle
        sx={{ ...typography.heading.s, color: semanticColors.text.primary }}
      >
        {t("settings.about")}
      </DialogTitle>
      <Divider />
      <DialogContent>
        {[
          {
            label: t("settings.browserSupported"),
            value: Video.isSupported ? t("common.yes") : t("common.no")
          },
          { label: t("settings.sdkVersion"), value: Video.version },
          { label: t("settings.appVersion"), value: packageJSON.version },
          {
            label: t("settings.deployedTag"),
            value: process.env.REACT_APP_GIT_TAG || "N/A"
          },
          {
            label: t("settings.deployedCommit"),
            value: process.env.REACT_APP_GIT_COMMIT || "N/A"
          },
          ...(roomType
            ? [{ label: t("settings.roomType"), value: roomType }]
            : [])
        ].map(({ label, value }) => (
          <Typography
            key={label}
            sx={{
              ...typography.body.m,
              color: semanticColors.text.primary,
              mb: 1
            }}
          >
            <strong>{label}:</strong> {value}
          </Typography>
        ))}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box
          component="button"
          onClick={onClose}
          autoFocus
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: semanticColors.gradient.surface,
            borderRadius: "40px",
            height: "44px",
            border: "none",
            cursor: "pointer",
            px: "24px"
          }}
        >
          <Typography
            sx={{
              ...typography.body.lBold,
              color: semanticColors.text.inverse
            }}
          >
            OK
          </Typography>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default AboutDialog;
